/**
 * @file SubscribersTab.tsx
 * @brief Super Admin → Analytics → Subscribers by price (W22), from B25
 *        `subscribers-by-price`.
 *
 * @details
 * Live Stripe plans grouped by the Price each account pays, next to today's
 * catalogue price, so an older price is visible. The period does not apply
 * (it is the live state). A row opens to its subscribers. Read-only: nobody
 * is moved from here.
 *
 * @author DES (prepared with Claude)
 * @date 25-10-2026
 */

import { Fragment, useState } from "react";
import { ChevronRight } from "lucide-react";
import { GridCell } from "@/features/analytics/components/GridBlock";
import { SkeletonRows } from "@/components/ui/Skeleton";
import { cn } from "@/shared/theme/cn";
import { formatCount, formatMoney, NULL_REASONS, planLabel } from "../businessMath";
import type { PriceGroup, SubscribersByPrice } from "../types";
import { DataTable, EmptyNote, ErrorNote, NullValue, SectionHeading, tdClass, tdNumClass } from "../components/common";

function dateText(iso: string | null): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

const groupId = (g: PriceGroup) => `${g.plan_code}|${g.billing_interval}|${g.currency}|${g.stripe_price_id ?? ""}`;

export function SubscribersTab({
  data,
  isLoading,
  error,
}: {
  data: SubscribersByPrice | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (error) return <ErrorNote error={error} what="subscribers by price" />;

  return (
    <GridCell padded={false}>
      <div className="px-4 py-4 md:px-5">
        <SectionHeading
          title="Subscribers by price"
          help="Live now; the period above does not apply. Each row is one Stripe Price; amounts are in the currency it charges, as Stripe holds them (Pro and Business are VAT-inclusive)."
          info={data?.definition}
        />
      </div>
      {isLoading ? (
        <SkeletonRows />
      ) : !data || data.groups.length === 0 ? (
        <EmptyNote>No live paid plans right now.</EmptyNote>
      ) : (
        <DataTable
          caption="Live subscribers grouped by Stripe price"
          head={[
            { label: "Plan / price", className: "min-w-[13rem]" },
            { label: "They pay", align: "right" },
            { label: "Today's price", align: "right" },
            { label: "Accounts", align: "right" },
            { label: "Seats", align: "right" },
            { label: "Paying", align: "right" },
            { label: "Trialing", align: "right" },
            { label: "Next renewal", align: "right" },
          ]}
        >
          {data.groups.map((g) => {
            const id = groupId(g);
            const isOpen = open.has(id);
            return (
              <Fragment key={id}>
                <tr className="border-t border-border/60 align-top">
                  <td className={tdClass}>
                    <button
                      type="button"
                      onClick={() => toggle(id)}
                      aria-expanded={isOpen}
                      className="flex items-start gap-1.5 text-left"
                    >
                      <ChevronRight
                        className={cn("mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform", isOpen && "rotate-90")}
                        aria-hidden
                      />
                      <span>
                        <span className="font-medium">
                          {planLabel(g.plan_code)} · {g.billing_interval === "year" ? "yearly" : "monthly"} · {g.currency}
                        </span>
                        <span className="mt-0.5 block font-mono text-[11px] text-muted-foreground">
                          {g.stripe_price_id ?? "(no price id)"}
                        </span>
                        <span
                          className={cn(
                            "mt-1 inline-block whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium",
                            g.is_current_catalogue ? "bg-success-soft text-success" : "bg-muted text-muted-foreground",
                          )}
                        >
                          {g.is_current_catalogue ? "Current price" : "Older price"}
                        </span>
                      </span>
                    </button>
                  </td>
                  <td className={tdNumClass}>
                    {g.unit_amount_minor == null ? (
                      <NullValue reason={g.unit_amount_reason ?? NULL_REASONS.unit_amount} className="justify-end" />
                    ) : (
                      <span title={`${g.unit_amount_minor} minor units`}>
                        {formatMoney(g.unit_amount_minor, g.currency)}
                        {g.tax_behavior ? (
                          <span className="block text-[11px] text-muted-foreground">
                            {g.tax_behavior === "inclusive" ? "VAT incl." : g.tax_behavior === "exclusive" ? "VAT excl." : g.tax_behavior}
                          </span>
                        ) : null}
                      </span>
                    )}
                  </td>
                  <td className={tdNumClass}>
                    {g.catalogue_unit_amount_minor == null ? (
                      <NullValue reason="No price setting for this plan, currency and interval." className="justify-end" />
                    ) : (
                      formatMoney(g.catalogue_unit_amount_minor, g.currency)
                    )}
                  </td>
                  <td className={tdNumClass}>{formatCount(g.accounts)}</td>
                  <td className={tdNumClass}>{formatCount(g.seats)}</td>
                  <td className={tdNumClass}>{formatCount(g.paying)}</td>
                  <td className={tdNumClass}>{formatCount(g.trialing)}</td>
                  <td className={tdNumClass}>{dateText(g.next_renewal_at)}</td>
                </tr>
                {isOpen
                  ? g.subscribers.map((s) => (
                      <tr key={s.subscription_id} className="text-xs text-muted-foreground">
                        <td className={`${tdClass} pl-11 md:pl-12`}>
                          <span className="text-foreground">{s.name ?? "–"}</span>{" "}
                          {s.account_type === "team" ? "(company)" : ""}
                        </td>
                        <td className={tdNumClass} colSpan={3}>
                          {s.status}
                          {s.renews ? "" : " · cancelling"}
                        </td>
                        <td className={tdNumClass}>{formatCount(s.seats)}</td>
                        <td className={tdNumClass} colSpan={2} />
                        <td className={tdNumClass}>
                          {s.renews ? "Renews" : "Ends"} {dateText(s.current_period_end)}
                        </td>
                      </tr>
                    ))
                  : null}
              </Fragment>
            );
          })}
        </DataTable>
      )}
    </GridCell>
  );
}
